/**
 * Apollo Retry Control System
 *
 * Hybrid system combining feature flag (remote control) and localStorage backoff
 * (local protection) to disable Apollo Client auto-retry during major incidents.
 *
 * Priority:
 * 1. Feature flag `apollo-retry-disabled` (checked first - allows immediate remote disable)
 * 2. localStorage backoff (checked second - provides local protection)
 *
 * Feature Flag Integration:
 * - Split.io SDK polls for updates every 60 seconds automatically
 * - React component (ApolloRetryFeatureFlagSync) syncs flag state to this module
 * - This module maintains state separate from React for use in Apollo links
 */

import { ApolloLink } from '@apollo/client'
import { map } from 'rxjs'

import * as retryBackoffModule from './storage/retryBackoff'

// Module-level state for feature flag
// This will be set by the Apollo client setup and checked during retries
let isRetryDisabledByFeatureFlag = false

// Wrapper for retryBackoff that can be mocked in tests
export const retryBackoff = {
  isInBackoff: (): boolean => retryBackoffModule.isInBackoff(),
}

/**
 * Set the feature flag state
 *
 * Called by ApolloRetryFeatureFlagSync React component when Split.io
 * detects a change to the 'apollo-retry-disabled' feature flag.
 * Split.io automatically polls every 60 seconds for updates.
 */
export function setFeatureFlagState(disabled: boolean): void {
  isRetryDisabledByFeatureFlag = disabled
}

/**
 * Get current feature flag state
 */
export function getFeatureFlagState(): boolean {
  return isRetryDisabledByFeatureFlag
}

/**
 * Main decision function: should Apollo Client retry this request?
 *
 * Returns false (do not retry) if:
 * - Feature flag `apollo-retry-disabled` is enabled (priority 1)
 * - Local backoff is active due to consecutive failures (priority 2)
 *
 * Returns true (retry allowed) otherwise
 */
export function shouldRetryRequest(): boolean {
  // Priority 1: Check feature flag (remote control)
  if (isRetryDisabledByFeatureFlag) {
    return false
  }

  // Priority 2: Check localStorage backoff (local protection)
  if (retryBackoff.isInBackoff()) {
    return false
  }

  // Allow retry if neither condition prevents it
  return true
}

/**
 * Reset the retry control state (useful for testing)
 */
export function resetRetryControlState(): void {
  isRetryDisabledByFeatureFlag = false
}

/**
 * Record a network failure if it's a trackable error
 *
 * Only tracks:
 * - True network failures (no status code) - timeouts, DNS failures, connection issues
 * - Server errors (5xx) - backend is struggling
 *
 * Ignores:
 * - Client errors (4xx) - bad requests won't be fixed by backing off
 *
 * @param networkError - The network error from Apollo Client
 * @returns true if backoff was triggered, false otherwise
 */
export function recordFailure(
  networkError?: Error & { statusCode?: number },
): boolean {
  // If no network error provided, don't track
  if (!networkError) {
    return false
  }

  const statusCode = networkError.statusCode

  // Track if:
  // - No status code (true network failure like timeout, DNS failure, etc.)
  // - Server error (5xx)
  const shouldTrack =
    statusCode === undefined || (statusCode >= 500 && statusCode < 600)

  if (shouldTrack) {
    return retryBackoffModule.recordFailure()
  }

  return false
}

/**
 * Record a successful request (delegates to retryBackoff module)
 * Exported for convenience - Apollo Client uses this
 */
export function recordSuccess(): void {
  retryBackoffModule.recordSuccess()
}

/**
 * Success Tracking Link
 *
 * Apollo Client middleware that tracks successful requests to reset
 * the retry backoff counter. This ensures that after a successful request,
 * the failure count is cleared and retry backoff is reset.
 *
 * Important: We only reset the backoff on COMPLETE success (no network errors
 * AND no GraphQL errors). This is intentional because:
 *
 * - GraphQL errors often indicate system stress (database timeouts, downstream
 *   service failures, backend resource exhaustion, resolver errors)
 * - During partial outages, some requests may return 200 OK but have GraphQL
 *   errors, indicating the system is still struggling
 * - Only fully successful requests (network + GraphQL) should disable the
 *   backoff protection
 *
 * This conservative approach ensures we maintain protection during degraded
 * states, not just complete outages.
 *
 * Usage: Add this to your Apollo Client link chain before the retry link.
 *
 * Related: CORE-3118
 */
export const SuccessTrackingLink = new ApolloLink((operation, forward) => {
  return forward(operation).pipe(
    map((response: ApolloLink.Result) => {
      // Only record success if there are NO errors at all (network or GraphQL)
      // GraphQL errors may indicate system degradation even if HTTP succeeded
      if (!response.errors || response.errors.length === 0) {
        recordSuccess()
      }

      return response
    }),
  )
})
